/**
 * 従業員 API クライアント
 */
import { parseApiError } from "../utils/apiError";
import { authFetch, API_BASE_URL } from "./client";

/**
 * 従業員に紐付いたタグ情報（一覧埋め込み）
 */
export interface EmployeeTagInfo {
    id: number;
    name: string | null;
    color: string | null;
}

/**
 * 従業員レスポンスの型
 */
export interface EmployeeResponse {
    id: number;
    code: string;
    name: string;
    store_id: number;
    department_id: number;
    employment_type_id: number;
    contract_hours: number | null;
    is_always_required: boolean;
    is_holiday_restricted: boolean;
    is_ensured_holiday: boolean;
    working_priority: number | null;
    nickname: string | null;
    employment_status: string | null;
    role: string | null;
    permission_level: number | null;
    admin_level: number;
    gender: string | null;
    birth_date: string | null;
    is_foreign_national: boolean;
    nationality: string | null;
    salary_type: string | null;
    salary_amount: number | null;
    is_short_time: boolean;
    social_insurance: string | null;
    memo: string | null;
    email: string | null;
    phone: string | null;
    is_active: boolean;
    display_order: number;
    created_at: string | null;
    updated_at: string | null;
    salary_display: string | null;
    permission_display: string | null;
    /** 部門名（「全部門」選択時のグルーピング用） */
    department_name: string | null;
    /** store_department.id（シフト書き込み API 用） */
    organization_id: number | null;
    /**
     * パスワード状態（employee_auth 由来）
     * - "normal": 正常（変更済み）
     * - "locked": ロック（未実装）
     * - "initial": 初期パスワード
     * - null: employee_auth 行が無い
     */
    password_status: "normal" | "locked" | "initial" | null;
    tags: EmployeeTagInfo[];
}

/**
 * 従業員一覧レスポンスの型
 */
export interface EmployeeListResponse {
    items: EmployeeResponse[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
}

/**
 * 従業員作成リクエストの型
 */
export interface EmployeeCreateRequest {
    code: string;
    name: string;
    store_id: number;
    department_id: number;
    employment_type_id: number;
    contract_hours?: number | null;
    is_always_required?: boolean;
    is_holiday_restricted?: boolean;
    is_ensured_holiday?: boolean;
    working_priority?: number | null;
    nickname?: string | null;
    employment_status?: string | null;
    role?: string | null;
    permission_level?: number | null;
    admin_level?: number | null;
    gender?: string | null;
    birth_date?: string | null;
    is_foreign_national?: boolean;
    nationality?: string | null;
    salary_type?: string | null;
    salary_amount?: number | null;
    is_short_time?: boolean;
    social_insurance?: string | null;
    memo?: string | null;
    email?: string | null;
    phone?: string | null;
}

/**
 * 従業員更新リクエストの型
 */
export interface EmployeeUpdateRequest {
    code?: string;
    name?: string;
    store_id?: number;
    department_id?: number;
    employment_type_id?: number;
    contract_hours?: number | null;
    is_always_required?: boolean;
    is_holiday_restricted?: boolean;
    is_ensured_holiday?: boolean;
    working_priority?: number | null;
    nickname?: string | null;
    employment_status?: string | null;
    role?: string | null;
    permission_level?: number | null;
    gender?: string | null;
    birth_date?: string | null;
    is_foreign_national?: boolean;
    nationality?: string | null;
    salary_type?: string | null;
    salary_amount?: number | null;
    is_short_time?: boolean;
    social_insurance?: string | null;
    memo?: string | null;
    email?: string | null;
    phone?: string | null;
    is_active?: boolean;
}

/**
 * 従業員一覧を取得
 */
export async function fetchEmployees(params?: {
    page?: number;
    page_size?: number;
    keyword?: string;
    store_id?: number;
    /** 複数店舗指定（カンマ区切り）。store_id より優先 */
    store_ids?: string;
    department_id?: number;
    employment_status?: string;
    employment_type_id?: number;
    is_active?: boolean;
}): Promise<EmployeeListResponse> {
    const searchParams = new URLSearchParams();

    if (params?.page) {searchParams.set("page", String(params.page));}
    if (params?.page_size) {searchParams.set("page_size", String(params.page_size));}
    if (params?.keyword) {searchParams.set("keyword", params.keyword);}
    if (params?.store_ids) {searchParams.set("store_ids", params.store_ids);}
    else if (params?.store_id) {searchParams.set("store_id", String(params.store_id));}
    if (params?.department_id) {searchParams.set("department_id", String(params.department_id));}
    if (params?.employment_status) {searchParams.set("employment_status", params.employment_status);}
    if (params?.employment_type_id) {searchParams.set("employment_type_id", String(params.employment_type_id));}
    if (params?.is_active !== undefined) {searchParams.set("is_active", String(params.is_active));}

    const queryString = searchParams.toString();
    const url = `${API_BASE_URL}/employees${queryString ? `?${queryString}` : ""}`;

    const response = await authFetch(url);

    if (!response.ok) {
        throw new Error(`Failed to fetch employees: ${response.status}`);
    }

    return response.json();
}

/**
 * 従業員詳細を取得
 */
export async function fetchEmployee(id: number): Promise<EmployeeResponse> {
    const response = await authFetch(`${API_BASE_URL}/employees/${id}`);

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error("従業員が見つかりません");
        }
        throw new Error(`Failed to fetch employee: ${response.status}`);
    }

    return response.json();
}

/**
 * 従業員コードで従業員を取得（ログイン用）
 */
export async function fetchEmployeeByCode(code: string): Promise<EmployeeResponse> {
    const response = await authFetch(`${API_BASE_URL}/employees/by-code/${encodeURIComponent(code)}`);

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error("ID に該当する従業員が見つかりません");
        }
        if (response.status === 403) {
            throw new Error("この従業員アカウントは無効です");
        }
        throw new Error(`従業員情報の取得に失敗しました: ${response.status}`);
    }

    return response.json();
}

/**
 * 従業員を作成
 */
export async function createEmployee(data: EmployeeCreateRequest): Promise<EmployeeResponse> {
    const response = await authFetch(`${API_BASE_URL}/employees`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw await parseApiError(response, "従業員の作成に失敗しました");
    }

    return response.json();
}

/**
 * 従業員を更新
 */
export async function updateEmployee(id: number, data: EmployeeUpdateRequest): Promise<EmployeeResponse> {
    const response = await authFetch(`${API_BASE_URL}/employees/${id}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw await parseApiError(response, "従業員の更新に失敗しました");
    }

    return response.json();
}

/**
 * 従業員を削除（論理削除）
 */
export async function deleteEmployee(id: number): Promise<void> {
    const response = await authFetch(`${API_BASE_URL}/employees/${id}`, {
        method: "DELETE",
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error("従業員が見つかりません");
        }
        throw new Error(`Failed to delete employee: ${response.status}`);
    }
}

/**
 * 従業員並び順アイテム
 */
export interface EmployeeOrderItem {
    id: number;
    display_order: number;
}

/**
 * 従業員の表示順を一括更新
 */
export async function updateEmployeeOrder(
    orders: EmployeeOrderItem[]
): Promise<{ message: string; count: number }> {
    const response = await authFetch(`${API_BASE_URL}/employees/order`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ orders }),
    });

    if (!response.ok) {
        throw new Error(`Failed to update employee order: ${response.status}`);
    }

    return response.json();
}

/**
 * 従業員のパスワードを初期パスワード（= login_id）に初期化する
 *
 * - password_hash: login_id のハッシュ
 * - password_changed_at: 本日
 * - must_change_password: 1（要変更）
 */
export async function resetEmployeePassword(
    employeeId: number,
): Promise<{ message: string }> {
    const response = await authFetch(
        `${API_BASE_URL}/employees/${employeeId}/reset-password`,
        { method: "POST" },
    );

    if (!response.ok) {
        throw await parseApiError(response, "パスワードの初期化に失敗しました");
    }

    return response.json();
}

/**
 * 異動登録用の型定義
 */
export interface TransferEmployeeParams {
    department_id: number;
    effective_date: string; // YYYY-MM-DD 形式
}

/**
 * 従業員の異動（部門変更）を登録する API
 */
export async function updateEmployeeTransfer(
    employeeId: number,
    params: TransferEmployeeParams
): Promise<void> {
    const response = await authFetch(`/api/employees/${employeeId}/transfer`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
    });

    if (!response.ok) {
        throw new Error("異動の登録に失敗しました。");
    }
}
